import React, { useState, useEffect, useCallback, useRef} from 'react';

const BLACKLISTED_ABILITIES = ['Astral Flight']; // Asol W

function QuizView() {
  const [championList, setChampionList] = useState([]);
  const recentChampionsRef = useRef([]);
  const [filteredChampionList, setFilteredChampionList] = useState([]);
  const [currentChampion, setCurrentChampion] = useState(null);
  const [currentAbility, setCurrentAbility] = useState(null);
  const [guess, setGuess] = useState('');
  const [feedback, setFeedback] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasGuessed, setHasGuessed] = useState(false);
  const [roleData, setRoleData] = useState({});
  const [selectedRole, setSelectedRole] = useState('all');
  const [flashColor, setFlashColor] = useState('black');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/data/champion.json`);
        const jsonData = await response.json();
        const championsArray = Object.values(jsonData.data);
        setChampionList(championsArray);
        setFilteredChampionList(championsArray);

        const roleResponse = await fetch(`${process.env.PUBLIC_URL}/data/roleChampionLists.json`);
        const roleJson = await roleResponse.json();
        setRoleData(roleJson);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    const storedRecords = JSON.parse(localStorage.getItem('cooldownRecords')) || [];
    setRecords(storedRecords);
  }, []);

  const filterChampionsByRole = (role) => {
    setSelectedRole(role);
    if (role === 'all') {
      setFilteredChampionList(championList);
    } else {
      const roleChampions = roleData[role] || [];
      const filtered = championList.filter((champ) => roleChampions.includes(champ.name));
      setFilteredChampionList(filtered);
    }
  };

  const generateNewQuestion = useCallback(async () => {
    setHasGuessed(false);
    setFeedback('');
    setGuess('');
  
    if (filteredChampionList.length === 0) return;
  
    const recentChampions = recentChampionsRef.current;
    const validChampions = filteredChampionList.filter(
      (champ) => !recentChampions.includes(champ.id)
    );
  
    if (validChampions.length === 0) return;
  
    let attempts = 0;
    const maxAttempts = 20;
  
    while (attempts < maxAttempts) {
      const randomChampion = validChampions[Math.floor(Math.random() * validChampions.length)];
  
      if (!randomChampion) return;
  
      try {
        const response = await fetch(
          `https://ddragon.leagueoflegends.com/cdn/14.21.1/data/en_US/champion/${randomChampion.id}.json`
        );
        const data = await response.json();
        const championData = data.data[randomChampion.id];
  
        const abilities = championData.spells;
        if (abilities && abilities.length > 0) {
          setCurrentChampion({
            name: championData.name,
            iconPath: `https://ddragon.leagueoflegends.com/cdn/14.21.1/img/champion/${championData.image.full}`,
          });
  
          let randomAbility;
          let randomIndex;
          let cooldownValue; // Declare cooldownValue here

          do {
            randomIndex = Math.floor(Math.random() * abilities.length);
            randomAbility = abilities[randomIndex];

            // Get cooldown or ammo recharge time
            const cooldown = randomAbility.cooldown;
            const ammoRechargeTime = randomAbility.ammo?.ammoRechargeTime;
            const useRechargeTime = Array.isArray(ammoRechargeTime) && ammoRechargeTime[0] > 0;

            cooldownValue = useRechargeTime
              ? ammoRechargeTime[0]
              : (Array.isArray(cooldown) ? cooldown[0] : 'Unknown');

            // Check if ability is blacklisted or has a cooldown of 2 or less
            if (
              BLACKLISTED_ABILITIES.includes(randomAbility.name) ||
              (typeof cooldownValue === 'number' && cooldownValue <= 2)
            ) {
              attempts++;
              continue;
            }
          } while (
            (BLACKLISTED_ABILITIES.includes(randomAbility.name) ||
              (typeof cooldownValue === 'number' && cooldownValue <= 2)) &&
            attempts < maxAttempts
          );
          if (attempts >= maxAttempts) return;
  
          const abilityKeys = ['Q', 'W', 'E', 'R'];
          const abilityKey = abilityKeys[randomIndex] || '';
  
          setCurrentAbility({
            name: randomAbility.name,
            iconPath: `https://ddragon.leagueoflegends.com/cdn/14.21.1/img/spell/${randomAbility.image.full}`,
            cooldown: cooldownValue,
            key: abilityKey,
          });
  
          recentChampionsRef.current = [...recentChampions, randomChampion.id].slice(-5); // Keep last 5
          return;
        }
      } catch (error) {
        attempts++;
        console.error('Error fetching ability data:', error);
      }
    }
  }, [filteredChampionList]);

  useEffect(() => {
    if (filteredChampionList.length > 0) {
      generateNewQuestion();
    }
  }, [filteredChampionList, generateNewQuestion]);

  const handleGuessSubmit = () => {
    if (guess === '' || !/^\d+(\.\d{1,2})?$/.test(guess) || parseFloat(guess) <= 0) {
      setFeedback('Please enter a valid positive number (up to two decimal places)!');
      return;
    }

    const actualCooldown = currentAbility.cooldown;
    const guessedCooldown = parseInt(guess);
    const difference = Math.abs(guessedCooldown - actualCooldown);

    if (difference === 0) {
      setFeedback('Perfect guess! You got it right!');
      setFlashColor('green');
    } else if (difference <= 3) {
      setFeedback(`Close! The actual cooldown is ${actualCooldown} seconds. Your guess was ${difference} seconds off.`);
      setFlashColor('orange');
    } else {
      setFeedback(`The actual cooldown is ${actualCooldown} seconds. Your guess was ${difference} seconds off.`);
      setFlashColor('red');
    }

    setTimeout(() => setFlashColor('black'), 500);

    const newRecord = {
      champion: currentChampion.name,
      ability: currentAbility.name,
      actualCooldown,
      guessedCooldown,
      difference,
    };
    const updatedRecords = [...records, newRecord];
    setRecords(updatedRecords);
    localStorage.setItem('cooldownRecords', JSON.stringify(updatedRecords));
    setHasGuessed(true);
  };

  const { averageError, totalGuesses } = records.length
    ? {
        averageError: (records.reduce((sum, r) => sum + r.difference, 0) / records.length).toFixed(2),
        totalGuesses: records.length,
      }
    : { averageError: 0, totalGuesses: 0 };

    const handleResetStats = () => {
      setRecords([]);
      setFeedback('Statistics have been reset!');
      localStorage.removeItem('cooldownRecords');
    };

  return (
    <div className="App">
      <div className="background-overlay">
        <img
          src={`${process.env.PUBLIC_URL}/perry_logo.png`}
          alt="Overlay"
          className="overlay-image"
        />
      </div>
      <div className="container">
        <div className="side-images left">
          <img src={`${process.env.PUBLIC_URL}/perry_fiddle.webp`} alt="Perry Fiddle" />
          <img src={`${process.env.PUBLIC_URL}/perry_nidalee.webp`} alt="Perry Nidalee" />
        </div>
  
        <div className="main-content">
          <div className="main-content-title">
            <img src={`${process.env.PUBLIC_URL}/title.png`} alt="Title" />
          </div>
          <h1>Cooldown Trainer</h1>
  
          {/* Role Filter Buttons with Images */}
          <div className="button-container">
            <img
              src={`${process.env.PUBLIC_URL}/all.png`}
              alt="All"
              className={`role-button ${selectedRole === 'all' ? 'selected' : ''}`}
              onClick={() => filterChampionsByRole('all')}
            />
            <img
              src={`${process.env.PUBLIC_URL}/top.png`}
              alt="Top"
              className={`role-button ${selectedRole === 'top' ? 'selected' : ''}`}
              onClick={() => filterChampionsByRole('top')}
            />
            <img
              src={`${process.env.PUBLIC_URL}/jungle.png`}
              alt="Jungle"
              className={`role-button ${selectedRole === 'jungle' ? 'selected' : ''}`}
              onClick={() => filterChampionsByRole('jungle')}
            />
            <img
              src={`${process.env.PUBLIC_URL}/mid.png`}
              alt="Mid"
              className={`role-button ${selectedRole === 'mid' ? 'selected' : ''}`}
              onClick={() => filterChampionsByRole('mid')}
            />
            <img
              src={`${process.env.PUBLIC_URL}/adc.png`}
              alt="ADC"
              className={`role-button ${selectedRole === 'adc' ? 'selected' : ''}`}
              onClick={() => filterChampionsByRole('adc')}
            />
            <img
              src={`${process.env.PUBLIC_URL}/support.png`}
              alt="Support"
              className={`role-button ${selectedRole === 'support' ? 'selected' : ''}`}
              onClick={() => filterChampionsByRole('support')}
            />
          </div>
  
          {loading ? (
            <p>Loading...</p>
          ) : currentChampion && currentAbility ? (
            <>
              <div className="icon-container">
                <div className="champion">
                  <img src={currentChampion.iconPath} alt={`${currentChampion.name} Icon`} />
                  <p>{currentChampion.name}</p>
                </div>
                <div className="ability">
                  <div className="ability-icon-container">
                    <img src={currentAbility.iconPath} alt={`${currentAbility.name} Icon`} />
                    <div className="ability-key-overlay">{currentAbility.key}</div>
                  </div>
                  <p>{currentAbility.name}</p>
                </div>
              </div>
              <h2 style={{ color: flashColor, transition: 'color 0.2s' }}>
                Guess this ability's RANK ONE cooldown in seconds:
              </h2>
              <div className="guess-input">
              <input
                type="text"
                value={guess}
                onChange={(e) => {
                  const input = e.target.value;
                  // Allow digits (0-9), optionally one dot, and up to two decimal places
                  if (/^\d*\.?\d{0,2}$/.test(input) && parseFloat(input) >= 0 && parseFloat(input) <= 500) {
                    setGuess(input);
                  } else if (input === '') {
                    setGuess('');
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (hasGuessed) {
                      generateNewQuestion();
                    } else {
                      handleGuessSubmit();
                    }
                  }
                }}
                placeholder="Cooldown.."
                readOnly={hasGuessed}
                // Optional: Add class to style the input when read-only
                className={hasGuessed ? 'input-read-only' : ''}
              />
              {!hasGuessed ? (
                <button onClick={handleGuessSubmit}>Submit Guess</button>
              ) : (
                <button onClick={generateNewQuestion}>Next Question</button>
              )}
            </div>
              <p>{feedback}</p>
              <div className="statistics">
                <h3>Your Stats</h3>
                <p>Total Guesses: {totalGuesses}</p>
                <p>Average Error: {averageError} seconds</p>
              </div>
              <button onClick={handleResetStats} className="reset-button">
                Reset Stats
              </button>
            </>
          ) : (
            <p>Loading data...</p>
          )}
        </div>
  
        {/* Right Side Images */}
        <div className="side-images right">
          <img src={`${process.env.PUBLIC_URL}/perry_lee.webp`} alt="Perry Lee" />
          <img src={`${process.env.PUBLIC_URL}/perry_viego.webp`} alt="Perry Viego" />
        </div>
      </div>
    </div>
  );
  
}

export default QuizView;
